import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiV1 } from "api-v2";
import { useCurrentCompanyId } from "hooks/auth";
import { showToast } from "utils/helpers";
import { unwrap } from "newui/utils/apiResponse";
import type { UserListItem } from "../../../types/userInvite";

interface ResendInviteArgs {
  reason?: string;
  userId: number;
}

interface ResendInviteError {
  code?: string;
  message: string;
}

const resendInvite = async ({ userId, reason }: ResendInviteArgs): Promise<UserListItem> => {
  const response = await apiV1.post<UserListItem>(
    `users/${userId}/invite/resend`,
    reason ? { reason } : {}
  );
  return unwrap<UserListItem>(response.data);
};

export const useResendInviteMutation = () => {
  const queryClient = useQueryClient();
  const companyId = useCurrentCompanyId();

  return useMutation<UserListItem, ResendInviteError, ResendInviteArgs>({
    mutationFn: resendInvite,
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users", companyId] });
      showToast("Invite resent successfully", "Success", true);
    },
    onError: (error) => {
      const message =
        error?.code === "RATE_LIMIT_EXCEEDED"
          ? "Too many resend attempts. Try again later."
          : error?.message || "Failed to resend invite. Please try again.";
      showToast(message, "Error");
    },
  });
};
